import React, { useEffect, useState, useRef } from "react";
import CustomTable from "../../components/Table";
import Header from "../../components/Header";
import AdminCointainer from "../../containers/Admin";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/AddButton";
import { Plus, vehicles } from "../../common/svg";
import { ApiService } from "../../service/api.service";
import { Column, VehicleData } from "../../service/interfaces/Table.interface";
import { MASTER_URL } from "../../common/common";


const Vehicle = () => {
  const navigate = useNavigate();
  const apiService = new ApiService();
  const [clientData, setClientData] = useState<VehicleData[]>([]);
  const [search, setSearch] = useState<string>("");
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const entityId = localStorage.getItem('entity_id');
  const menuRef = useRef<HTMLDivElement>(null);
  // const { showToast } = useToast();

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const columns: Column<VehicleData>[] = [
    { header: "Vehicle Type", dataKey: "vehicleType" },
    { header: "Vehicle Model", dataKey: "vehicleModel" },
    { header: "Vehicle Year", dataKey: "vehicleNumber" },
    { header: "Vehicle Make", dataKey: "vehicleMake" },
    { header: "Owner Name", dataKey: "ownerName" },
    { header: "Owner Contact", dataKey: "ownerContact" },
    {
      header: "Actions",
      dataKey: "vehicleId",
      render: (value, row) => (
        <div className="relative" ref={activeMenu === value ? menuRef : null}>
          <button
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click event
              setActiveMenu(activeMenu === value ? null : value);
            }}
          >
            <div className="flex flex-col items-center justify-center h-5">
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            </div>
          </button>

          {/* Dropdown Menu */}
          {activeMenu === value && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200">
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row);
                  setActiveMenu(null);
                }}
              >
                Edit
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row);
                  setActiveMenu(null);
                }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  const handleEdit = (item: VehicleData) => {
    navigate(`managevehicle/${item.vehicleId}`);
  };

  const handleDelete = (item: VehicleData) => {
    if (window.confirm(`Are you sure you want to delete ${""}?`)) {
      // deleteClient(item.clientId);
    }
  };

  const deleteClient = async (clientId: string) => {
    try {
      const response: any = await apiService.deleteAPi(`${MASTER_URL}/vehicle/get-route/${clientId}`);
      if (response.status === "200") {
        // Refresh the client list after deletion
        // showToast("Client deleted successfully", "success");
        getClientData();
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      // showToast("Failed to delete client", "error");
    }
  };

  const getClientData = async (isLoadingMore = false) => {
    // Only fetch if there's more data to load
    if (!hasMoreData && isLoadingMore) return;

    setLoading(true);

    // Check if MASTER_URL is defined, if not use fallback
    const baseUrl = MASTER_URL || 'http://localhost:3001';
    const apiUrl = `${baseUrl}/vehicle/get-route?page=${currentPage}&limit=${pageSize}&search=${search}`;

    try {
      // Add userid header for the API
      const headers = {
        'userid': entityId || '1' // Use entityId as userid or default to '1'
      };
      const response: any = await apiService.getApiWithHeaders(apiUrl, headers);

      if (response && response.status === "200") {
        if (response.body?.data && Array.isArray(response.body.data)) {
          const formattedData = response.body.data.map((client: any) => {
            return {
              vehicleType: client.vehicleType || "N/A",
              vehicleNumber: client.vehicleYear || "N/A",
              vehicleModel: client.vehicleModel || "N/A",
              vehicleMake: client.vehicleMake || "N/A",
              vehicleId: client.vehicleId || "N/A",
              ownerName: client.ownerName || "N/A",
              ownerContact: client.phoneNumber || "N/A",
            };
          });

          // Determine if there's more data
          const totalRecords = parseInt(response.body.pagination?.totalRecords) || 0;
          const currentLoadedItems = isLoadingMore
            ? clientData.length + formattedData.length
            : formattedData.length;

          // Update has more data flag
          setHasMoreData(currentLoadedItems < totalRecords);

          // For infinite scroll, append new data if loading more
          setClientData(prevData =>
            isLoadingMore ? [...prevData, ...formattedData] : formattedData
          );

          setTotalItems(totalRecords);
        } else {
          setClientData([]);
          setTotalItems(0);
        }
      } else {
        setClientData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
      setHasMoreData(false);
      setClientData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    // Only load more if there's more data
    if (hasMoreData) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getClientData(true);
    }
  };

  useEffect(() => {
    // Reset has more data and fetch initial data when search or entity changes
    setHasMoreData(true);
    getClientData();
  }, [search, entityId]);



  const handleRowClick = (item: VehicleData) => {
    // navigate(`view/${item.clientId}`);
  };

  const handleAdd = () => {
    navigate("managevehicle");
  };

  return (
    <div className="">
      <Header icon={vehicles} title="Vehcile" showSearch={true} setSearch={setSearch}>
        <div className="flex space-x-2">
          <CustomButton label="" onClick={handleAdd} icon={Plus} />
          <button
            onClick={() => navigate('/vehicles/livepin-tracking')}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <span>LivePin Tracking</span>
          </button>
        </div>
      </Header>

      <AdminCointainer>
        <CustomTable<VehicleData>
          columns={columns}
          data={clientData}
          onRowClick={handleRowClick}
          loading={loading}
          onLoadMore={handleLoadMore}
        />
      </AdminCointainer>
    </div>
  );
};

export default Vehicle;